<template>
  <Layout>
    <div class="no-print">
      <h2>
        {{ "daily_report" | translate }} #{{ dailyReport.id }}
        <div
          class="badge badge-pill badge-secondary"
          title="has unsaved changes"
          v-if="hasUnsavedChanges"
        >
          <i class="fa fa-fw fa-edit"></i>
        </div>
      </h2>

      <div class="mb-3">
        <button class="btn btn-primary mr-1" @click="save">
          {{ "save" | translate }}
        </button>
        <button class="btn btn-secondary" @click="cancel">
          {{ "cancel" | translate }}
        </button>
      </div>

      <div class="mb-3">
        <label>{{ "please_select_date" | translate }}</label>
        <air-datepicker
          target-class="form-control"
          v-model="dailyReport.date"
        ></air-datepicker>
      </div>

      <div class="mb-3">
        <label>{{ "please_type_your_name" | translate }}</label>
        <input type="text" v-model="dailyReport.buyer" class="form-control" />
      </div>

      <div class="mb-3 d-flex" v-if="dailyReport.buyer">
        <div class="mr-3">
          <button class="btn btn-primary mr-1" @click="newPurchaseOrderItem">
            {{ "purchase" | translate }}
          </button>

          <button class="btn btn-primary mr-1" @click="newReserveOrderItem">
            {{ "reserve" | translate }}
          </button>

          <button class="btn btn-primary mr-1" @click="newRefundOrderItem">
            {{ "refund" | translate }}
          </button>

          <button class="btn btn-primary mr-1" @click="newGiftCardOrderItem">
            {{ "gift_card" | translate }}
          </button>
        </div>
        <div class="mr-3">
          <button class="btn btn-primary mr-1" @click="newBatchPurchaseOrderItems">
            {{ "batch_purchase" | translate }}
          </button>
          <button class="btn btn-primary mr-1" @click="newBatchReserveOrderItems">
            {{ "batch_reserve" | translate }}
          </button>
        </div>
        <div class="mr-3">
          <button class="btn btn-success mr-1" @click="newReceiveItem">
            {{ "receive" | translate }}
          </button>
          <button class="btn btn-danger mr-1" @click="newGiveItem">
            {{ "give" | translate }}
          </button>
        </div>

        <div>
          <button class="btn btn-secondary mr-1" @click="updateTransactions">
            {{ "refresh" | translate }}
          </button>
        </div>
      </div>
    </div>

    <div class="page-break-around" v-if="dailyReport.purchaseOrderItems.length">
      <h3>{{ "purchase_orders" | translate }}</h3>

      <part-purchase-order-item-list
        :purchase-order-items="dailyReport.purchaseOrderItems"
        @update="updateTransactions"
      ></part-purchase-order-item-list>
    </div>

    <div class="page-break-around" v-if="dailyReport.reserveOrderItems.length">
      <h3>{{ "reserve_orders" | translate }}</h3>
      <part-reserve-order-item-list
        :reserve-order-items="dailyReport.reserveOrderItems"
        @update="updateTransactions"
      ></part-reserve-order-item-list>
    </div>

    <div class="page-break-around" v-if="dailyReport.refundOrderItems.length">
      <h3>{{ "refund_orders" | translate }}</h3>
      <part-refund-order-item-list
        :refund-order-items="dailyReport.refundOrderItems"
        @update="updateTransactions"
      ></part-refund-order-item-list>
    </div>

    <div class="page-break-around" v-if="dailyReport.giftCardOrderItems.length">
      <h3>{{ "gift_card_orders" | translate }}</h3>
      <part-gift-card-order-item-list
        :gift-card-order-items="dailyReport.giftCardOrderItems"
        @update="updateTransactions"
      ></part-gift-card-order-item-list>
    </div>

    <div
      class="page-break-around"
      v-if="dailyReport.giveItems.length || dailyReport.receiveItems.length"
    >
      <div v-if="dailyReport.receiveItems.length">
        <h3>{{ "receive_items" | translate }}</h3>
        <part-receive-item-list
          :receive-items="dailyReport.receiveItems"
          @update="updateTransactions"
        ></part-receive-item-list>
      </div>

      <div v-if="dailyReport.giveItems.length">
        <h3>{{ "give_items" | translate }}</h3>
        <part-give-item-list
          :give-items="dailyReport.giveItems"
          @update="updateTransactions"
        ></part-give-item-list>
      </div>
    </div>

    <div>
      <h3>{{ "account_summary" | translate }}</h3>
      <part-account-summary :summary="accountSummaries"></part-account-summary>

      <div class="page-break-around no-print card card-primary mb-3">
        <div class="card-body">
          <h3>{{ "account_detail" | translate }}</h3>
          <div v-for="(data, index) in accountSummaries" :key="index">
            <div class="page-break-around">
              <h4>{{ "account" | translate }} @{{ data[0] | translate }}</h4>
              <part-transaction-summary
                :transactions="data[1]"
              ></part-transaction-summary>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import { ModalService } from "@/modules/xs-modal";
import ModalPurchaseOrderItemForm from "./ModalPurchaseOrderItemForm";
import ModalReserveOrderItemForm from "./ModalReserveOrderItemForm";
import ModalRefundOrderItemForm from "./ModalRefundOrderItemForm";
import ModalGiftCardOrderItemForm from "./ModalGiftCardOrderItemForm";
import ModalReceiveItemForm from "./ModalReceiveItemForm";
import ModalGiveItemForm from "./ModalGiveItemForm";
import { today } from "../components/AirDatepicker.vue";
import PartPurchaseOrderItemList from "./PartPurchaseOrderItemList";
import PartReserveOrderItemList from "./PartReserveOrderItemList";
import PartRefundOrderItemList from "./PartRefundOrderItemList";
import PartGiftCardOrderItemList from "./PartGiftCardOrderItemList";
import PartReceiveItemList from "./PartReceiveItemList";
import PartAccountSummary from "./PartAccountSummary";
import PartGiveItemList from "./PartGiveItemList";
import PartTransactionSummary from "./PartTransactionSummary";
import DailyReportService from "@/services/DailyReportService";
import store from "store";
import _ from "lodash";
import { mapState } from "vuex";
import FirebaseDailyReport from "@/services/FirebaseDailyReport";
import { ID_NOT_SAVED } from "@/config";
import ModalBatchReserveOrderItemForm from "./ModalBatchReserveOrderItemForm.vue";
import ModalBatchPurchaseOrderItemForm from "./ModalBatchPurchaseOrderItemForm.vue";

const BACKUP_DAILY_REPORT = "temp_backup_daily_report";

export default {
  data() {
    return {
      dailyReportId: this.$route.query["id"],
      dailyReport: {
        id: ID_NOT_SAVED,
        date: today(),
        buyer: null,
        purchaseOrderItems: [],
        reserveOrderItems: [],
        refundOrderItems: [],
        giftCardOrderItems: [],
        receiveItems: [],
        giveItems: [],

        transactions: [],
        timestamp: new Date().getTime(),
      },
      untouched: null,
    };
  },

  computed: {
    accountSummaries() {
      return DailyReportService.groupTransactions(
        this.dailyReport.transactions
      );
    },
    ...mapState({
      user: (state) => state.user,
    }),
    hasUnsavedChanges() {
      return (
        this.untouched && this.untouched.timestamp != this.dailyReport.timestamp
      );
    },
  },
  created() {
    this.loadData();
  },

  methods: {
    async loadData() {
      if (this.dailyReportId) {
        let doc = await FirebaseDailyReport.find(this.dailyReportId);
        this.dailyReport = doc.data();
      }
      this.untouched = _.cloneDeep(this.dailyReport);
      this.resume();
    },
    resume() {
      let backup = store.get(BACKUP_DAILY_REPORT);
      if (!backup) {
        return;
      }

      // when do replace?
      // 1. all new form, simple replace
      if ((!backup.id || backup.id === ID_NOT_SAVED) && !this.dailyReportId) {
        this.dailyReport = backup;
        return;
      }

      // 2. all saved form, same id, and backup is newer
      if (
        backup.id === this.dailyReportId &&
        backup.timestamp > this.dailyReport.timestamp
      ) {
        this.dailyReport = backup;
        return;
      }
    },
    newPurchaseOrderItem() {
      ModalService.open(ModalPurchaseOrderItemForm).then(
        (purchaseOrderItem) => {
          this.dailyReport.purchaseOrderItems.push(purchaseOrderItem);
          this.updateTransactions();
        }
      );
    },

    newReserveOrderItem() {
      ModalService.open(ModalReserveOrderItemForm).then((reserveOrderItem) => {
        this.dailyReport.reserveOrderItems.push(reserveOrderItem);
        this.updateTransactions();
      });
    },
    newRefundOrderItem() {
      ModalService.open(ModalRefundOrderItemForm).then((refundOrderItem) => {
        this.dailyReport.refundOrderItems.push(refundOrderItem);
        this.updateTransactions();
      });
    },
    newGiftCardOrderItem() {
      ModalService.open(ModalGiftCardOrderItemForm).then(
        (giftCardOrderItem) => {
          this.dailyReport.giftCardOrderItems.push(giftCardOrderItem);
          this.updateTransactions();
        }
      );
    },
    newReceiveItem() {
      ModalService.open(ModalReceiveItemForm).then((receiveItem) => {
        this.dailyReport.receiveItems.push(receiveItem);
        this.updateTransactions();
      });
    },
    newGiveItem() {
      ModalService.open(ModalGiveItemForm).then((giveItem) => {
        this.dailyReport.giveItems.push(giveItem);
        this.updateTransactions();
      });
    },

    newBatchReserveOrderItems() {
      ModalService.open(ModalBatchReserveOrderItemForm).then((reserveOrderItems) => {
        for (let i = 0; i < reserveOrderItems.length; i ++) {
          this.dailyReport.reserveOrderItems.push(reserveOrderItems[i])
        }
        this.updateTransactions()
      })
    },

    newBatchPurchaseOrderItems() {
      ModalService.open(ModalBatchPurchaseOrderItemForm).then((purchaseOrderItems) => {
        for (let i = 0; i < purchaseOrderItems.length; i ++) {
          this.dailyReport.purchaseOrderItems.push(purchaseOrderItems[i])
        }
        this.updateTransactions()
      })
    },

    updateTransactions() {
      this.dailyReport.timestamp = new Date().getTime();
      this.dailyReport.transactions = DailyReportService.collectTransactions(
        this.dailyReport
      );
      store.set(BACKUP_DAILY_REPORT, this.dailyReport);
    },

    save() {
      this.dailyReport.timestamp = new Date().getTime();
      FirebaseDailyReport.save(this.dailyReport).then(() => {
        store.set(BACKUP_DAILY_REPORT, null);
        this.$router.push("/dailyReports/" + this.dailyReport.id);
      });
    },
    cancel() {
      store.set(BACKUP_DAILY_REPORT, null);
      this.$router.push("/dailyReports/" + (this.dailyReportId || ""));
    },
  },

  components: {
    PartPurchaseOrderItemList,
    PartReserveOrderItemList,
    PartRefundOrderItemList,
    PartGiftCardOrderItemList,
    PartReceiveItemList,
    PartGiveItemList,
    PartTransactionSummary,
    PartAccountSummary,
  },
};
</script>
